import { nombreTurno } from '../../utils/turnos';
// src/components/calendario/MyCalendar.js
// Calendario de la Agenda. Las tareas viven en la BD (antes en localStorage) y se mezclan con
// eventos del sistema de solo lectura: pagos a proveedores (vencimientos) y depósito pendiente.
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import { getAgenda, crearTarea, actualizarTarea, importarTareas, completarTarea, reabrirTarea } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import './MyCalendar.css';
import SucursalCampo from '../sucursales/SucursalCampo';

moment.locale('es');
const localizer = momentLocalizer(moment);

const emptyEvent = {
  title:        '',
  description:  '',
  date:         '',
  startTime:    '',
  endTime:      '',
  priority:     'Media',
  alert:        false,
  alertTime:    'Al momento',
  alertChannel: 'Pestaña emergente',
  frequency:    'Nunca',
  category:     '',
  status:       'Pendiente',
  visibility:   'all',
  branchId:     '',
  ownerEmail:   '',
  seguimiento:  'fecha',
  completadoAt: null,
  completadoPor: null,
  repeatUntil:  '',     // "Repetir hasta" (solo al crear una actividad recurrente)
  alcance:      'esta', // al editar una serie: esta / siguientes / serie
  serieId:      null
};
// Repetición: valor guardado (columna frecuencia) → texto del formulario.
const REPETICIONES = [['Nunca', 'No repetir'], ['Diario', 'Diariamente'], ['Semanal', 'Semanalmente'], ['Mensual', 'Mensualmente']];
const textoRepeticion = f => (REPETICIONES.find(([v]) => v === f) || REPETICIONES[0])[1];
const fechaCorta = ymd => (ymd ? ymd.split('-').reverse().join('/') : '');
const HASTA_COMPLETAR = 'hasta_completar';
const fechaHora = d => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '');

// Dia
const WEEKDAYS = ['dom','lun','mar','mié','jue','vie','sáb'];


const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const dd = n => String(n).padStart(2, '0');

// ===== Toolbar personalizado =====
function MyToolbar({ date, view, label, onNavigate, onView, showAddButton }) {
  const [isNarrow, setIsNarrow] = useState(window.matchMedia('(max-width: 400px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 400px)');
    const h = e => setIsNarrow(e.matches);
    mq.addEventListener ? mq.addEventListener('change', h) : mq.addListener(h);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', h) : mq.removeListener(h));
  }, []);

  const m = moment(date);
  let primary = label;     // línea 1
  let secondary = '';      // línea 2 (solo en semana)

  if (view === 'month') {
    // Mes + Año (se mantiene como pediste)
    primary = capitalize(m.format('MMMM YYYY'));
  } else if (view === 'week') {
    // Línea 1: mes | Línea 2: (DD - DD)
    const start = moment(m).startOf('week'); // lunes por locale
    const end   = moment(m).endOf('week');
    if (start.isoWeekday() === 7) start.add(1, 'day');
    if (end.isoWeekday() === 6) end.add(1, 'day');
    primary = capitalize(m.format('MMMM'));
    secondary = `(${dd(start.date())} - ${dd(end.date())})`;
  } else if (view === 'day') {
    primary = `${dd(m.date())} de ${capitalize(m.format('MMMM'))}`;
  } else if (view === 'agenda') {
    primary = 'Agenda';
  }

  const semTxt = isNarrow ? 'Sem' : 'Semana';

  return (
    <div className="rbc-toolbar d-flex justify-content-between align-items-center mb-3 my-toolbar">
      <div className="rbc-btn-group">
        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => onNavigate('PREV')}>‹</button>
        {/* Vuelve al periodo actual (TODAY lleva la fecha a hoy, así que en vista mensual muestra el mes actual). */}
        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => onNavigate('TODAY')}>{view === 'week' ? 'Esta semana' : view === 'day' ? 'Hoy' : 'Este mes'}</button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => onNavigate('NEXT')}>›</button>
      </div>

      <div className="rbc-toolbar-label fw-bold toolbar-title">
        <div className="title-primary">{primary}</div>
        {view === 'week' && <div className="title-sub">{secondary}</div>}
      </div>

      <div className="rbc-btn-group d-flex align-items-center">
        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => onView('month')}>Mes</button>
        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => onView('week')}>{semTxt}</button>
        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => onView('day')}>Día</button>
        <button className="btn btn-outline-secondary btn-sm me-3" onClick={() => onView('agenda')}>Agenda</button>
        {showAddButton ? null : null}
      </div>
    </div>
  );
}

// Tarea de la API → evento del calendario (mismos campos que usaba el formulario).
const tareaAEvento = t => ({
  kind: 'tarea', id: t.id, title: t.titulo, description: t.descripcion,
  start: new Date(t.inicioAt), end: new Date(t.finAt), priority: t.prioridad, status: t.estado,
  category: t.categoria, frequency: t.frecuencia, alert: !!t.alerta?.activa,
  alertTime: t.alerta?.momento || 'Al momento', alertChannel: t.alerta?.canal || 'Pestaña emergente',
  visibility: t.visibilidad, branchId: t.sucursalId || '',
  seguimiento: t.seguimiento || 'fecha', completadoAt: t.completadoAt, completadoPor: t.completadoPor,
  serieId: t.serieId || null, serieHasta: t.serieHasta || null,
});
// "Hasta completar": todo el día, siempre en su propia fecha. Si sigue pendiente y la fecha ya pasó,
// se marca como atrasada en ese día (no se mueve a hoy); el seguimiento la sigue listando hasta completarla.
const ajustarHastaCompletar = e => {
  if (e.seguimiento !== HASTA_COMPLETAR) return e;
  const atrasada = e.status !== 'Completado' && moment(e.start).isBefore(moment().startOf('day'));
  const dia = moment(e.start).startOf('day').toDate();
  return { ...e, allDay: true, atrasada, fechaOriginal: e.start, titleOriginal: e.title, start: dia, end: dia,
    title: `${e.status === 'Completado' ? '✓ ' : '⏳ '}${e.title}${atrasada ? ' (atrasada)' : ''}` };
};
const eventoATarea = e => ({
  titulo: e.title, descripcion: e.description || '', inicioAt: e.start.toISOString(), finAt: e.end.toISOString(),
  prioridad: e.priority, estado: e.status, categoria: e.category || '', frecuencia: e.frequency || 'Nunca',
  alerta: { activa: !!e.alert, momento: e.alertTime, canal: e.alertChannel }, visibilidad: e.visibility || 'all',
  sucursalId: e.visibility === 'branch' ? (e.branchId || null) : null,
  seguimiento: e.seguimiento === HASTA_COMPLETAR ? HASTA_COMPLETAR : 'fecha',
});
// Inicio/fin según el tipo de seguimiento ("hasta completar" no usa horas: ocupa el día).
const rangoForm = ({ seguimiento, date: day, startTime, endTime }) => (seguimiento === HASTA_COMPLETAR
  ? { start: new Date(`${day}T00:00`), end: new Date(`${day}T23:59`) }
  : { start: new Date(`${day}T${startTime}`), end: new Date(`${day}T${endTime}`) });
const fechaLocal = iso => { const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); };

// Rango a pedir según la fecha visible (cubre mes, semana, día y la vista agenda de 30 días).
const rangoDe = date => ({
  desde: moment(date).startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
  hasta: moment(date).endOf('month').add(37, 'days').format('YYYY-MM-DD'),
});

// oculto: no muestra la grilla ni consulta eventos (Vista de agenda), pero el formulario de actividad sigue disponible.
const MyCalendar = forwardRef(({ showAddButton = true, sucursalId, sucursales = [], onVerProveedor, onVerDeposito, onVerCierre, onTareasChanged, oculto = false }, ref) => {
  const [events, setEvents]       = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newEvent, setNewEvent]   = useState(emptyEvent);
  const [date, setDate]           = useState(new Date());

  const [currView, setCurrView] = useState('month');

  const role = (localStorage.getItem('role') || 'viewer').toLowerCase();
  const isAdmin = role === 'admin';
  const currentEmail = localStorage.getItem('email') || '';
  const activeSucursalId = sucursalId && sucursalId !== 'all' ? sucursalId : ''; // "todas" no es una sucursal para una actividad

  const popupRefs = useRef({});
  const { desde, hasta } = rangoDe(date);

  const cargar = useCallback(async () => {
    if (!sucursalId || oculto) return;
    try {
      const a = await getAgenda(sucursalId, desde, hasta);
      setEvents([
        ...a.tareas.map(tareaAEvento).map(ajustarHastaCompletar),
        ...a.pagos.map(p => ({ kind: 'pago', id: 'pago-' + p.id, proveedorId: p.proveedorId, estado: p.estado, allDay: true,
          title: `${p.estado === 'Pagada' ? '✓ ' : ''}Pago ${p.proveedor}${p.referencia ? ' · ' + p.referencia : ''} · ${toMoney(p.estado === 'Pagada' ? p.total : p.saldo)}`,
          start: fechaLocal(p.vencimiento), end: fechaLocal(p.vencimiento) })),
        // Cierres con efectivo por liquidar: en la fecha del cierre de origen (sin inventar fecha de depósito).
        ...(a.liquidaciones || []).map(l => ({ kind: 'liquidacion', id: 'liq-' + l.cierreId, cierreId: l.cierreId, allDay: true,
          title: `Pendiente: liquidar cierre ${l.sucursal} ${nombreTurno(l.turnoId).toLowerCase()} – ${toMoney(l.pendiente)}`,
          start: fechaLocal(l.fecha), end: fechaLocal(l.fecha) })),
        // Cierres con una acción pendiente del administrador (por revisar / en corrección), en la fecha del cierre.
        ...(a.revisiones || []).map(r => ({ kind: 'revision', id: 'rev-' + r.cierreId, cierreId: r.cierreId, estado: r.estado, allDay: true,
          title: `${r.estado}: cuadre ${r.sucursal} ${nombreTurno(r.turnoId).toLowerCase()}${r.cajero ? ' · ' + r.cajero : ''}`,
          start: fechaLocal(r.fecha), end: fechaLocal(r.fecha) })),
        ...(a.deposito ? [{ kind: 'deposito', id: 'deposito', allDay: true, title: `Depósito pendiente · ${toMoney(a.deposito.monto)}`,
          start: fechaLocal(a.deposito.fecha), end: fechaLocal(a.deposito.fecha) }] : []),
      ]);
    } catch (e) {
      console.error(e);
    }
  }, [sucursalId, desde, hasta, oculto]);
  useEffect(() => { cargar(); }, [cargar]);

  // Importación única de las actividades que este navegador tenía guardadas en localStorage.
  useEffect(() => {
    if (!isAdmin || !sucursalId) return;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem('events') || 'null'); } catch {}
    if (!Array.isArray(saved) || !saved.length) return;
    const tareas = saved.filter(e => e && e.title && e.start && e.end).map(e => ({
      ...eventoATarea({ ...emptyEvent, ...e, start: new Date(e.start), end: new Date(e.end) }),
      origenLocal: `${e.start}|${e.title}`,
    }));
    importarTareas(sucursalId, tareas).then(() => {
      localStorage.setItem('events_importados', localStorage.getItem('events'));
      localStorage.removeItem('events');
      cargar();
    }).catch(console.error);
  }, [isAdmin, sucursalId, cargar]);

  useEffect(() => {
    if (modalOpen) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [modalOpen]);

  const setBranchId = useCallback(id => setNewEvent(evt => (evt.branchId === id ? evt : { ...evt, branchId: id })), []);
  const handleChange = field => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setNewEvent(evt => ({ ...evt, [field]: val }));
  };

  const openAddModal = () => {
    if (!isAdmin) return;
    setIsEditing(false);
    setNewEvent({ ...emptyEvent, ownerEmail: currentEmail, branchId: activeSucursalId, date: moment(date).format('YYYY-MM-DD') });
    setModalOpen(true);
  };

  // openEditTarea: abre el mismo formulario desde la Vista de agenda (tarea tal como la devuelve la API).
  useImperativeHandle(ref, () => ({ openAddModal, reload: cargar, openEditTarea: t => openEditModal(ajustarHastaCompletar(tareaAEvento(t))) }));

  const openEditModal = (event) => {
    if (!isAdmin) return;
    const day       = moment(event.fechaOriginal || event.start).format('YYYY-MM-DD');
    const startTime = moment(event.start).format('HH:mm');
    const endTime   = moment(event.end).format('HH:mm');
    setIsEditing(true);
    setNewEvent({ ...emptyEvent, ...event, title: event.titleOriginal ?? event.title, date: day, startTime, endTime, alcance: 'esta', repeatUntil: '' });
    setModalOpen(true);
  };

  const showPopup = (eventoId, title, description) => {
    const popupName = `popup_event_${eventoId}`;
    const width = 400, height = 250;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top  = window.screenY + (window.innerHeight - height) / 3;
    let popup;
    if (popupRefs.current[eventoId] && !popupRefs.current[eventoId].closed) {
      popup = popupRefs.current[eventoId];
    } else {
      popup = window.open('', popupName, `width=${width},height=${height},left=${left},top=${top}`);
      popupRefs.current[eventoId] = popup;
    }
    if (popup) {
      popup.document.title = `Recordatorio: ${title}`;
      popup.document.body.style.cssText = 'margin:0;font-family:Arial,sans-serif;display:flex;flex-direction:column;justify-content:center;align-items:center;height:100vh;background-color:#f8f9fa';
      const box = popup.document.createElement('div');
      box.style.cssText = 'text-align:center;padding:1rem';
      const h2 = popup.document.createElement('h2'); h2.textContent = '🔔 Recordatorio';
      const h4 = popup.document.createElement('h4'); h4.textContent = title;
      const p = popup.document.createElement('p'); p.textContent = description || '';
      const btn = popup.document.createElement('button'); btn.textContent = 'Cerrar';
      btn.style.cssText = 'margin-top:1.5rem;padding:8px 16px;font-size:1rem;background-color:#ca9e3fff;color:white;border:none;border-radius:4px;cursor:pointer';
      btn.onclick = () => popup.close();
      box.append(h2, h4, p, btn);
      popup.document.body.replaceChildren(box);
      popup.focus();
    } else {
      window.alert(`🔔 Recordatorio: ${title}\n${description || ''}`);
    }
  };

  const handleSave = async () => {
    const { title, description, date: day, startTime, endTime, alertTime, visibility, seguimiento } = newEvent;
    const hastaCompletar = seguimiento === HASTA_COMPLETAR;
    if (hastaCompletar ? (!title || !day) : (!title || !day || !startTime || !endTime)) return window.alert(hastaCompletar ? 'Completa título y fecha' : 'Completa título, fecha e horas');
    const { start, end } = rangoForm(newEvent);
    if (end < start) return window.alert('La hora de fin debe ser posterior a la de inicio');
    const alert = hastaCompletar ? false : newEvent.alert;
    const branchId = visibility === 'branch' ? (newEvent.branchId || activeSucursalId) : '';
    if (visibility === 'branch' && !branchId) return window.alert('Selecciona la sucursal de la actividad');
    // Repetición (solo al crear): "Repetir hasta" es obligatorio e inclusivo, y no puede ser anterior a la fecha.
    const repite = !isEditing && newEvent.frequency && newEvent.frequency !== 'Nunca';
    if (repite && !newEvent.repeatUntil) return Swal.fire('Repetición', 'Indica hasta qué fecha se repite la actividad.', 'warning');
    if (repite && newEvent.repeatUntil < day) return Swal.fire('Repetición', '"Repetir hasta" no puede ser anterior a la fecha de la actividad.', 'warning');
    const payload = {
      ...eventoATarea({ ...newEvent, start, end, branchId, alert,
        ...(hastaCompletar ? { status: newEvent.status === 'Completado' ? 'Completado' : 'Pendiente' } : {}) }),
      ...(repite ? { repeticion: { frecuencia: newEvent.frequency, hasta: newEvent.repeatUntil } } : {}),
      ...(isEditing && newEvent.serieId ? { alcance: newEvent.alcance } : {}),
    };
    try {
      const saved = isEditing ? await actualizarTarea(sucursalId, newEvent.id, payload) : await crearTarea(sucursalId, payload);
      setModalOpen(false);
      if (saved?.ocurrencias > 1) Swal.fire('Actividad recurrente', `Se crearon ${saved.ocurrencias} actividades (${textoRepeticion(newEvent.frequency).toLowerCase()} hasta el ${fechaCorta(newEvent.repeatUntil)}).`, 'success');
      else if (saved?.actualizadas > 1) Swal.fire('Serie actualizada', `Se actualizaron ${saved.actualizadas} actividades de la serie.`, 'success');
      await cargar();
      onTareasChanged?.();
      if (alert) {
        const alertDate = new Date(start);
        switch (alertTime) {
          case '10 minutos antes': alertDate.setMinutes(alertDate.getMinutes() - 10); break;
          case '1 hora antes':     alertDate.setHours(alertDate.getHours() - 1); break;
          case '1 día antes':      alertDate.setDate(alertDate.getDate() - 1); break;
          case '3 días antes':     alertDate.setDate(alertDate.getDate() - 3); break;
          default: break;
        }
        const diffMs = alertDate.getTime() - Date.now();
        const fire = () => showPopup(saved.id, title, description);
        diffMs > 0 ? setTimeout(fire, diffMs) : fire();
      }
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudo guardar la actividad.', 'error');
    }
  };

  // No se borra: se marca como cancelada y deja de mostrarse (queda en la BD como historial).
  const handleDelete = async () => {
    if (!isEditing) return;
    const textoAlcance = !newEvent.serieId || newEvent.alcance === 'esta' ? '¿Quitar esta actividad de la agenda?'
      : newEvent.alcance === 'serie' ? '¿Quitar todas las actividades de esta serie?' : '¿Quitar esta actividad y las siguientes de la serie?';
    const ok = await Swal.fire({ title: 'Eliminar actividad', text: textoAlcance, icon: 'warning', showCancelButton: true, confirmButtonText: 'Eliminar' });
    if (!ok.isConfirmed) return;
    try {
      if (popupRefs.current[newEvent.id] && !popupRefs.current[newEvent.id].closed) popupRefs.current[newEvent.id].close();
      await actualizarTarea(sucursalId, newEvent.id, { ...eventoATarea({ ...newEvent, ...rangoForm(newEvent) }), estado: 'Cancelada',
        ...(newEvent.serieId ? { alcance: newEvent.alcance } : {}) });
      setModalOpen(false);
      await cargar();
      onTareasChanged?.();
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudo eliminar.', 'error');
    }
  };

  // Completar / reabrir (actividades "hasta completar"): guarda fecha y hora de finalización.
  const cambiarEstado = async (completar) => {
    if (!completar) {
      const ok = await Swal.fire({ title: 'Reabrir actividad', text: 'Volverá a pendientes activos.', icon: 'question', showCancelButton: true, confirmButtonText: 'Reabrir' });
      if (!ok.isConfirmed) return;
    }
    try {
      await (completar ? completarTarea : reabrirTarea)(sucursalId, newEvent.id);
      setModalOpen(false);
      await cargar();
      onTareasChanged?.();
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudo actualizar la actividad.', 'error');
    }
  };

  // Colores por tipo y estado (pagos vencidos en rojo, completados en verde tenue).
  const eventPropGetter = event => {
    if (event.kind === 'pago') {
      const c = { Vencida: 'var(--danger)', Pagada: 'var(--ok-muted)', Parcial: 'var(--partial)' }[event.estado] || 'var(--info)';
      return { style: { backgroundColor: c, borderColor: c } };
    }
    if (event.kind === 'deposito') return { style: { backgroundColor: 'var(--event-deposit)', borderColor: 'var(--event-deposit)' } };
    if (event.kind === 'liquidacion') return { style: { backgroundColor: 'var(--warn)', borderColor: 'var(--warn)' } };
    if (event.kind === 'revision') {
      const c = event.estado === 'Por revisar' ? 'var(--event-review)' : 'var(--event-correction)';
      return { style: { backgroundColor: c, borderColor: c } };
    }
    if (event.atrasada) return { style: { backgroundColor: 'var(--danger)', borderColor: 'var(--danger)' } };
    if (event.status === 'Completado') return { style: { backgroundColor: 'var(--ok-faded)', borderColor: 'var(--ok-faded)', textDecoration: 'line-through' } };
    return {};
  };

const formats = useMemo(() => ({
  // Encabezado de la fila de días (semana/mes) abreviado
   weekdayFormat: (date) => WEEKDAYS[moment(date).day()],

  dayHeaderFormat: (date) => `${capitalize(WEEKDAYS[moment(date).day()])} ${dd(moment(date).date())}`,

  // 🔹 Este era el responsable de mostrar “13”
  //     Lo cambiamos SOLO en la vista day
  dayFormat: (date) =>
    currView === 'day'
      ? capitalize(moment(date).format('dddd')) // "Sábado"
      : moment(date).format('D'),

  monthHeaderFormat: (date) => capitalize(moment(date).format('MMMM YYYY')),

  dayRangeHeaderFormat: ({ start, end }) => {
    const ms = moment(start), me = moment(end);
    return `${capitalize(ms.format('D MMM'))} – ${me.format('D MMM')}`;
  },

  timeGutterFormat: (date) => moment(date).format('HH:mm'),

  agendaHeaderFormat: ({ start, end }) => {
    const s = moment(start), e = moment(end);
    return `Agenda: ${s.format('D [de] MMMM')} – ${e.format('D [de] MMMM')}`;
  },

  agendaDateFormat: (date) => `${capitalize(WEEKDAYS[moment(date).day()])} ${dd(moment(date).date())}`,

  agendaTimeRangeFormat: ({ start, end }) =>
    `${moment(start).format('HH:mm')} – ${moment(end).format('HH:mm')}`,
}), [currView]);


  return (
    <div className="p-3">
      <div className="calendar-container" hidden={oculto}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          defaultView="month"
          date={date}
          onNavigate={d => setDate(d)}
          eventPropGetter={eventPropGetter}
          views={['month', 'week', 'day', 'agenda']}
          style={{ height: 640 }}
          components={{ toolbar: props => <MyToolbar {...props} showAddButton={showAddButton} /> }}
          formats={formats}
          onView={(v) => setCurrView(v)}
          onSelectEvent={event => {
            if (event.kind === 'pago') return onVerProveedor?.(event.proveedorId, String(event.id).replace(/^pago-/, '')); // id de la cuenta por pagar
            if (event.kind === 'deposito') return onVerDeposito?.();
            if (event.kind === 'liquidacion' || event.kind === 'revision') return onVerCierre?.(event.cierreId);
            openEditModal(event);
          }}
          dayPropGetter={date => {
            const today = new Date();
            if (date.getDate()===today.getDate() && date.getMonth()===today.getMonth() && date.getFullYear()===today.getFullYear()) {
              return { className: 'today-cell' };
            }
            return {};
          }}
          messages={{
            date: 'Fecha',
            time: 'Hora',
            event: 'Evento',
            allDay: 'Todo el día',
            week: 'Semana',
            work_week: 'Semana laboral',
            day: 'Día',
            month: 'Mes',
            previous: '‹',
            next: '›',
            yesterday: 'Ayer',
            tomorrow: 'Mañana',
            today: 'Hoy',
            agenda: 'Agenda',
            noEventsInRange: 'No hay eventos en este rango',
            showMore: total => `+ ${total} más`,
          }}
          scrollToTime={new Date(1970, 1, 1, 7, 0, 0)}
        />
      </div>
      {/* Modal de actividad */}
      {modalOpen && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isEditing ? 'Editar Actividad' : 'Agregar Actividad'}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Título</label>
                    <input type="text" className="form-control" value={newEvent.title} onChange={handleChange('title')} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Descripción</label>
                    <textarea className="form-control" rows={2} value={newEvent.description} onChange={handleChange('description')} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Tipo de seguimiento</label>
                    <select className="form-select" value={newEvent.seguimiento} onChange={handleChange('seguimiento')}>
                      <option value="fecha">Por fecha y hora</option>
                      <option value={HASTA_COMPLETAR}>Hasta completar</option>
                    </select>
                    {newEvent.seguimiento === HASTA_COMPLETAR && <small className="text-muted">Sigue pendiente aunque pase la fecha, hasta que la marques como completada.</small>}
                  </div>
                  {newEvent.seguimiento === HASTA_COMPLETAR ? (
                    <div className="mb-2">
                      <label className="form-label">Fecha</label>
                      <input type="date" className="form-control" value={newEvent.date} onChange={handleChange('date')} />
                    </div>
                  ) : (
                  <div className="row mb-2">
                    <div className="col">
                      <label className="form-label">Fecha</label>
                      <input type="date" className="form-control" value={newEvent.date} onChange={handleChange('date')} />
                    </div>
                    <div className="col">
                      <label className="form-label">Hora inicio – fin</label>
                      <div className="d-flex">
                        <input type="time" className="form-control" value={newEvent.startTime} onChange={handleChange('startTime')} />
                        <span className="mx-2 align-self-center">–</span>
                        <input type="time" className="form-control" value={newEvent.endTime} onChange={handleChange('endTime')} />
                      </div>
                    </div>
                  </div>
                  )}
                  {/* Repetición: al crear genera una actividad por fecha; al editar una serie se elige a cuáles aplicar. */}
                  {!isEditing ? (
                    <div className="row mb-2">
                      <div className="col">
                        <label className="form-label">Repetición</label>
                        <select className="form-select" value={newEvent.frequency} onChange={handleChange('frequency')}>
                          {REPETICIONES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                        </select>
                      </div>
                      {newEvent.frequency !== 'Nunca' && (
                        <div className="col">
                          <label className="form-label">Repetir hasta</label>
                          <input type="date" className="form-control" value={newEvent.repeatUntil} min={newEvent.date || undefined} onChange={handleChange('repeatUntil')} />
                        </div>
                      )}
                    </div>
                  ) : newEvent.serieId && (
                    <div className="mb-2">
                      <label className="form-label">Aplicar cambios a</label>
                      <select className="form-select" value={newEvent.alcance} onChange={handleChange('alcance')}>
                        <option value="esta">Solo esta actividad</option>
                        <option value="siguientes">Esta y las siguientes</option>
                        <option value="serie">Toda la serie</option>
                      </select>
                      <small className="text-muted">Repetición: {textoRepeticion(newEvent.frequency).toLowerCase()}{newEvent.serieHasta ? ` hasta el ${fechaCorta(newEvent.serieHasta)}` : ''}. En las demás actividades se conserva su fecha.</small>
                    </div>
                  )}
                  <div className="mb-2">
                    <label className="form-label">Prioridad</label>
                    <select className="form-select" value={newEvent.priority} onChange={handleChange('priority')}>
                      <option value="Alta">Alta 🔴</option>
                      <option value="Media">Media 🟠</option>
                      <option value="Baja">Baja 🟢</option>
                    </select>
                  </div>
                  {newEvent.seguimiento !== HASTA_COMPLETAR && <>
                  <div className="form-check mb-2">
                    <input type="checkbox" className="form-check-input" id="alertCheck" checked={newEvent.alert} onChange={handleChange('alert')} />
                    <label className="form-check-label" htmlFor="alertCheck">¿Deseas recibir alerta?</label>
                  </div>
                  {newEvent.alert && (
                    <div className="mb-2">
                      <label className="form-label">Momento alerta</label>
                      <select className="form-select" value={newEvent.alertTime} onChange={handleChange('alertTime')}>
                        <option value="Al momento">Al momento</option>
                        <option value="10 minutos antes">10 minutos antes</option>
                        <option value="1 hora antes">1 hora antes</option>
                        <option value="1 día antes">1 día antes</option>
                        <option value="3 días antes">3 días antes</option>
                      </select>
                    </div>
                  )}
                  </>}
                  <div className="mb-2">
                    <label className="form-label">Categoría</label>
                    <input type="text" className="form-control" placeholder='Ej. "Trabajo", "Personal"' value={newEvent.category} onChange={handleChange('category')} />
                  </div>
                  {newEvent.seguimiento === HASTA_COMPLETAR ? (
                    <div className="mb-2">
                      <label className="form-label d-block">Estado</label>
                      <span className={`fx-badge fx-badge--${newEvent.status === 'Completado' ? 'completado' : 'hoy'}`}>{newEvent.status === 'Completado' ? 'Completada' : 'Pendiente'}</span>
                      {newEvent.status === 'Completado' && newEvent.completadoAt && (
                        <small className="text-muted ms-2">Finalizada el {fechaHora(newEvent.completadoAt)}{newEvent.completadoPor ? ` por ${newEvent.completadoPor}` : ''}</small>)}
                    </div>
                  ) : (
                  <div className="mb-2">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={newEvent.status} onChange={handleChange('status')}>
                      <option value="Pendiente">Pendiente</option>
                      <option value="En curso">En curso</option>
                      <option value="Completado">Completado</option>
                    </select>
                  </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label d-block">Visibilidad de actividad</label>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="visibility" id="visMine" value="mine" checked={newEvent.visibility === 'mine'} onChange={handleChange('visibility')} />
                      <label className="form-check-label" htmlFor="visMine">Solo para mi</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="visibility" id="visAll" value="all" checked={newEvent.visibility === 'all'} onChange={handleChange('visibility')} />
                      <label className="form-check-label" htmlFor="visAll">Todos</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="visibility" id="visBranch" value="branch"
                        checked={newEvent.visibility === 'branch'}
                        onChange={e => {
                          const val = e.target.value;
                          setNewEvent(evt => ({
                            ...evt,
                            visibility: val,
                            branchId: evt.branchId || (activeSucursalId && activeSucursalId !== 'all' ? activeSucursalId : '')
                          }));
                        }} />
                      <label className="form-check-label" htmlFor="visBranch">Por sucursal</label>
                    </div>
                    {newEvent.visibility === 'branch' && (
                      <div className="mt-2">
                        {/* Fija si hay una sucursal global; a elegir con "Todas las sucursales". */}
                        <SucursalCampo className="agenda-sucursal-campo" value={newEvent.branchId} onChange={setBranchId} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                  {isEditing && <button type="button" className="btn btn-danger me-auto" onClick={handleDelete}>🗑 Eliminar</button>}
                  {isEditing && newEvent.seguimiento === HASTA_COMPLETAR && (newEvent.status === 'Completado'
                    ? <button type="button" className="btn btn-outline-secondary" onClick={() => cambiarEstado(false)}>Reabrir</button>
                    : <button type="button" className="btn btn-outline-primary" onClick={() => cambiarEstado(true)}>✓ Completar</button>)}
                  <button type="button" className="btn btn-primary" onClick={handleSave}>{isEditing ? 'Actualizar' : 'Guardar'}</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default MyCalendar;
